import { useCallback, useEffect, useRef, useState } from 'react';
import styled from '@emotion/styled/macro';
import { useTranslation } from 'react-i18next';
import { Colors } from '@robinpowered/design-system';

type SignatureCaptureProps = {
  /**
   * Called with a PNG data URI of the captured signature whenever it changes,
   * and with null when the pad is empty or cleared.
   */
  onSignatureChange: (dataUrl: string | null) => void;
};

type SignatureMode = 'draw' | 'type';

const STROKE_COLOR = Colors.Gray100;
const STROKE_WIDTH = 2.5;
// Rendered at 2x for crispness wherever the uploaded image is displayed.
const TYPED_EXPORT_HEIGHT = 200;
const TYPED_EXPORT_FONT_SIZE = 96;
const TYPED_EXPORT_PADDING = 48;
const SIGNATURE_FONT_FAMILY =
  '"Snell Roundhand", "Apple Chancery", "Segoe Script", "Brush Script MT", cursive';

/**
 * Signature capture with two input modes: draw with a finger or mouse, or
 * type a name. Both modes report the signature as a PNG data URI so the
 * parent can send it with the agreement.
 */
export const SignatureCapture = ({
  onSignatureChange,
}: SignatureCaptureProps): JSX.Element => {
  const { t } = useTranslation('documentAgreement');
  const [mode, setMode] = useState<SignatureMode>('draw');

  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const isDrawingRef = useRef(false);
  const hasStrokesRef = useRef(false);
  const [hasStrokes, setHasStrokes] = useState(false);

  const [typedName, setTypedName] = useState('');

  const getContext = () => canvasRef.current?.getContext('2d') ?? null;

  // Size the canvas backing store to the rendered element, accounting for
  // device pixel ratio so strokes stay crisp on retina screens.
  useEffect(() => {
    if (mode !== 'draw') {
      return;
    }
    const canvas = canvasRef.current;
    const ctx = getContext();
    if (!canvas || !ctx) {
      return;
    }
    const { width, height } = canvas.getBoundingClientRect();
    const scale = window.devicePixelRatio || 1;
    canvas.width = width * scale;
    canvas.height = height * scale;
    ctx.scale(scale, scale);
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = STROKE_COLOR;
    ctx.lineWidth = STROKE_WIDTH;
  }, [mode]);

  // The pad is tinted gray, so strokes are drawn on a transparent canvas and
  // composed onto a white background at export time to stay legible wherever
  // the uploaded image is rendered.
  const exportDrawnSignature = (canvas: HTMLCanvasElement): string => {
    const output = document.createElement('canvas');
    output.width = canvas.width;
    output.height = canvas.height;
    const ctx = output.getContext('2d');
    if (!ctx) {
      return canvas.toDataURL('image/png');
    }
    ctx.fillStyle = Colors.White0;
    ctx.fillRect(0, 0, output.width, output.height);
    ctx.drawImage(canvas, 0, 0);
    return output.toDataURL('image/png');
  };

  const pointerPosition = (event: React.PointerEvent<HTMLCanvasElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    return {
      x: event.clientX - rect.left,
      y: event.clientY - rect.top,
    };
  };

  const handlePointerDown = (event: React.PointerEvent<HTMLCanvasElement>) => {
    const ctx = getContext();
    if (!ctx) {
      return;
    }
    event.currentTarget.setPointerCapture(event.pointerId);
    const { x, y } = pointerPosition(event);
    ctx.beginPath();
    ctx.moveTo(x, y);
    // Draw a dot so a single tap still leaves a mark.
    ctx.lineTo(x, y);
    ctx.stroke();
    isDrawingRef.current = true;
    hasStrokesRef.current = true;
  };

  const handlePointerMove = (event: React.PointerEvent<HTMLCanvasElement>) => {
    const ctx = getContext();
    if (!ctx || !isDrawingRef.current) {
      return;
    }
    const { x, y } = pointerPosition(event);
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const handlePointerUp = (event: React.PointerEvent<HTMLCanvasElement>) => {
    if (!isDrawingRef.current) {
      return;
    }
    isDrawingRef.current = false;
    event.currentTarget.releasePointerCapture(event.pointerId);
    setHasStrokes(hasStrokesRef.current);
    if (canvasRef.current && hasStrokesRef.current) {
      onSignatureChange(exportDrawnSignature(canvasRef.current));
    }
  };

  const clearDrawnSignature = () => {
    const canvas = canvasRef.current;
    const ctx = getContext();
    if (!canvas || !ctx) {
      return;
    }
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    isDrawingRef.current = false;
    hasStrokesRef.current = false;
    setHasStrokes(false);
    onSignatureChange(null);
  };

  const exportTypedSignature = useCallback((name: string): string | null => {
    const trimmed = name.trim();
    if (!trimmed) {
      return null;
    }
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      return null;
    }
    const font = `${TYPED_EXPORT_FONT_SIZE}px ${SIGNATURE_FONT_FAMILY}`;
    ctx.font = font;
    const textWidth = ctx.measureText(trimmed).width;
    canvas.width = Math.ceil(textWidth) + TYPED_EXPORT_PADDING * 2;
    canvas.height = TYPED_EXPORT_HEIGHT;
    // Canvas state resets when the element is resized
    ctx.font = font;
    ctx.fillStyle = Colors.White0;
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.fillStyle = Colors.Gray100;
    ctx.textBaseline = 'middle';
    ctx.fillText(trimmed, TYPED_EXPORT_PADDING, TYPED_EXPORT_HEIGHT / 2);
    return canvas.toDataURL('image/png');
  }, []);

  const handleTypedNameChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const name = event.target.value;
    setTypedName(name);
    onSignatureChange(exportTypedSignature(name));
  };

  const clearTypedSignature = () => {
    setTypedName('');
    onSignatureChange(null);
  };

  // Each mode keeps its own state; report whichever mode is active so the
  // agreement always matches what the guest sees.
  const changeMode = (nextMode: SignatureMode) => {
    if (nextMode === mode) {
      return;
    }
    setMode(nextMode);
    if (nextMode === 'draw') {
      // The draw canvas remounts empty, so previous strokes are gone.
      hasStrokesRef.current = false;
      setHasStrokes(false);
      onSignatureChange(null);
    } else {
      onSignatureChange(exportTypedSignature(typedName));
    }
  };

  const tabs: { key: SignatureMode; label: string; icon: JSX.Element }[] = [
    { key: 'draw', label: t('signature_tab_draw'), icon: <DrawIcon /> },
    { key: 'type', label: t('signature_tab_type'), icon: <TypeIcon /> },
  ];

  return (
    <div>
      <TabList role="tablist">
        {tabs.map(({ key, label, icon }) => (
          <TabButton
            key={key}
            type="button"
            role="tab"
            aria-selected={mode === key}
            isActive={mode === key}
            onClick={() => changeMode(key)}
          >
            {icon}
            {label}
          </TabButton>
        ))}
      </TabList>

      <SignaturePad>
        {mode === 'draw' && (
          <>
            <SignatureCanvas
              ref={canvasRef}
              aria-label={t('signature_input_label')}
              onPointerDown={handlePointerDown}
              onPointerMove={handlePointerMove}
              onPointerUp={handlePointerUp}
              onPointerCancel={handlePointerUp}
            />
            <SignatureBaseline>
              <ClearButton
                type="button"
                aria-label={t('clear_signature')}
                onClick={clearDrawnSignature}
                disabled={!hasStrokes}
              >
                ×
              </ClearButton>
              <BaselineRule />
            </SignatureBaseline>
          </>
        )}

        {mode === 'type' && (
          <SignatureBaseline>
            <ClearButton
              type="button"
              aria-label={t('clear_signature')}
              onClick={clearTypedSignature}
              disabled={!typedName}
            >
              ×
            </ClearButton>
            <TypedSignatureInput
              value={typedName}
              onChange={handleTypedNameChange}
              placeholder={t('type_signature_placeholder')}
              aria-label={t('signature_input_label')}
            />
          </SignatureBaseline>
        )}
      </SignaturePad>
    </div>
  );
};

const DrawIcon = () => (
  <TabIcon viewBox="0 0 16 16" aria-hidden="true">
    <path
      d="M11.1 1.9a1.7 1.7 0 0 1 2.4 2.4l-7.6 7.6-3.2.8.8-3.2 7.6-7.6z"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinejoin="round"
    />
    <path
      d="M1.5 14.5c2 0 2.5-1.4 4-1.4s2 1.4 4 1.4 2.5-1.4 4.5-1.4"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.2"
      strokeLinecap="round"
    />
  </TabIcon>
);

const TypeIcon = () => (
  <TabIcon viewBox="0 0 16 16" aria-hidden="true">
    <text
      x="0"
      y="12.5"
      fontSize="13"
      fontWeight="600"
      fill="currentColor"
      fontFamily="inherit"
    >
      T
    </text>
    <text
      x="8.5"
      y="12.5"
      fontSize="9"
      fontWeight="600"
      fill="currentColor"
      fontFamily="inherit"
    >
      T
    </text>
  </TabIcon>
);

const TabIcon = styled.svg`
  width: 16px;
  height: 16px;
  flex-shrink: 0;
`;

const TabList = styled.div`
  display: flex;
  gap: 8px;
  border-bottom: 1px solid ${Colors.Gray10};
  margin-bottom: 24px;
`;

const TabButton = styled.button<{ isActive: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  background: none;
  border: none;
  cursor: pointer;
  font-size: 16px;
  color: ${({ isActive }) => (isActive ? Colors.Gray100 : Colors.Gray60)};
  font-weight: ${({ isActive }) => (isActive ? 500 : 400)};
  /* Keep the active indicator on top of the list's bottom border */
  margin-bottom: -1px;
  border-bottom: 2px solid
    ${({ isActive }) => (isActive ? Colors.Blue100 : 'transparent')};

  &:focus-visible {
    /* A11y on focus */
    outline: 2px solid ${Colors.Maroon100};
    border-radius: 4px;
  }
`;

const SignaturePad = styled.div`
  position: relative;
  width: 100%;
  height: 180px;
  background-color: ${Colors.Gray5};
  border: 1px solid ${Colors.Gray10};
  border-radius: 8px;
  overflow: hidden;
`;

const SignatureCanvas = styled.canvas`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  /* Draw with a finger without scrolling the page on touch screens */
  touch-action: none;
`;

const SignatureBaseline = styled.div`
  position: absolute;
  left: 20px;
  right: 20px;
  bottom: 20px;
  display: flex;
  align-items: center;
  gap: 8px;
  /* The baseline is decorative; strokes land on the canvas above it */
  pointer-events: none;
`;

const ClearButton = styled.button`
  pointer-events: auto;
  background: none;
  border: none;
  padding: 0 4px;
  font-size: 20px;
  line-height: 1;
  color: ${Colors.Gray40};
  cursor: pointer;

  &:disabled {
    cursor: default;
  }

  &:focus {
    /* A11y on focus */
    outline: 2px solid ${Colors.Maroon100};
    border-radius: 4px;
  }
`;

const BaselineRule = styled.div`
  flex: 1;
  border-bottom: 1px solid ${Colors.Gray40};
`;

const TypedSignatureInput = styled.input`
  pointer-events: auto;
  flex: 1;
  background: none;
  border: none;
  border-bottom: 1px solid ${Colors.Gray40};
  border-radius: 0;
  padding: 0 4px 4px 4px;
  font-family: ${SIGNATURE_FONT_FAMILY};
  font-size: 32px;
  color: ${Colors.Gray100};

  &::placeholder {
    color: ${Colors.Gray40};
    font-size: 24px;
  }

  &:focus {
    outline: none;
    border-bottom-color: ${Colors.Gray100};
  }
`;
